import React, { useEffect, useState } from 'react';
import { getRequestDetail, getRequestResult, getRequests, reprocessRequest } from '../services/api';
import ScoreLegend from './ScoreLegend';
import MatchDetails from './MatchDetails';
import { modeBadgeClass, modeShortLabel, modeTableTooltip } from '../utils/modeUI';

function buildShareUrl(requestId, expId) {
  const base = `${window.location.origin}${window.location.pathname}`;
  return `${base}?view=share&req=${encodeURIComponent(requestId)}&exp=${encodeURIComponent(expId)}`;
}

function buildRequestShareUrl(requestId) {
  const base = `${window.location.origin}${window.location.pathname}`;
  return `${base}?view=share&req=${encodeURIComponent(requestId)}`;
}

export default function RequestDetail({ requestId, onBack }) {
  const [data, setData] = useState(null);
  const [resultJson, setResultJson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showJson, setShowJson] = useState(false);
  const [expandedExp, setExpandedExp] = useState(null);
  const [expandedAward, setExpandedAward] = useState(null);
  const [activePanel, setActivePanel] = useState('matched');
  const [reprocessing, setReprocessing] = useState(false);
  const [copiedExpId, setCopiedExpId] = useState(null);
  const [copiedRequestLink, setCopiedRequestLink] = useState(false);
  const [showUnmatchedExp, setShowUnmatchedExp] = useState({});
  const [expandedCandidateExp, setExpandedCandidateExp] = useState({});

  // Pagination
  const [expPage, setExpPage]         = useState(0);
  const [expPageSize, setExpPageSize] = useState(10);

  // Side-by-side state
  const [siblingData, setSiblingData] = useState(null);
  const [activeMode, setActiveMode]   = useState(null);

  useEffect(() => {
    setLoading(true);
    setResultJson(null);
    setShowJson(false);
    setExpandedExp(null);
    setExpandedAward(null);
    setActivePanel('matched');
    setSiblingData(null);
    setActiveMode(null);
    setExpPage(0);
    getRequestDetail(requestId).then(d => {
      setData(d);
      if (d.group_id) {
        getRequests(1, 10, '', '').then(res => {
          const sibling = (res.requests || []).find(
            r => r.group_id === d.group_id && r.request_id !== d.request_id
          );
          if (sibling) getRequestDetail(sibling.request_id).then(setSiblingData);
        });
      }
    }).finally(() => setLoading(false));
  }, [requestId]);

  // Auto-poll while processing
  useEffect(() => {
    if (!data || data.state === 'COMPLETED' || data.state === 'FAILED') return;
    const id = setInterval(() => {
      getRequestDetail(requestId).then(d => {
        setData(d);
        if (d.state === 'COMPLETED' || d.state === 'FAILED') clearInterval(id);
      });
    }, 3000);
    return () => clearInterval(id);
  }, [data, requestId]);

  // Poll sibling
  useEffect(() => {
    if (!siblingData || siblingData.state === 'COMPLETED' || siblingData.state === 'FAILED') return;
    const id = setInterval(() => {
      getRequestDetail(siblingData.request_id).then(d => {
        setSiblingData(d);
        if (d.state === 'COMPLETED' || d.state === 'FAILED') clearInterval(id);
      });
    }, 3000);
    return () => clearInterval(id);
  }, [siblingData]);

  useEffect(() => {
    if (data?.state === 'COMPLETED') {
      getRequestResult(requestId).then(setResultJson).catch(console.error);
    }
  }, [data?.state, requestId]);

  // Reset to page 0 when the active dataset changes
  useEffect(() => { setExpPage(0); }, [activeMode]);

  if (loading) return <div className="processing-indicator"><div className="spinner" /><span>Loading...</span></div>;
  if (!data)   return <div className="empty-state">Request not found.</div>;

  const displayData   = activeMode && siblingData && siblingData.matching_mode === activeMode ? siblingData : data;
  const mode          = displayData.matching_mode || 'llm';
  const expenditures  = displayData.expenditures || [];
  const results       = displayData.results || [];

  // ── Pagination slice ─────────────────────────────────────────────────────
  const totalExp    = expenditures.length;
  const totalPages  = Math.ceil(totalExp / expPageSize);
  const pageStart   = expPage * expPageSize;
  const pagedExps   = expenditures.slice(pageStart, pageStart + expPageSize);

  return (
    <div className="detail-page">
      {/* Compact Header Bar */}
      <div className="detail-bar">
        <button className="back-btn" onClick={onBack}>Back</button>
        <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.request_id?.substring(0, 16)}</span>
        <span className={`badge ${data.state?.toLowerCase()}`}>{data.state}</span>
        <span className={`badge ${modeBadgeClass(mode)}`}>{mode.toUpperCase()}</span>
        {data.processing_time_ms != null && <span className="processing-time">{data.processing_time_ms}ms</span>}
        {data.source_files?.expenditure_file?.file_name && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }} title="Source file">
            {data.source_files.expenditure_file.file_name}
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <ScoreLegend />
          {data.state === 'COMPLETED' && (
            <button
              className="detail-toggle-btn"
              onClick={() => {
                const url = buildRequestShareUrl(data.request_id);
                navigator.clipboard.writeText(url).then(() => {
                  setCopiedRequestLink(true);
                  setTimeout(() => setCopiedRequestLink(false), 2000);
                });
              }}
              title="Copy a shareable link to all request results"
              style={{ fontSize: 10, padding: '3px 10px' }}
            >
              {copiedRequestLink ? '✓ Copied!' : '🔗 Share Request'}
            </button>
          )}
          {data.state === 'COMPLETED' && (
            <button className="detail-toggle-btn" onClick={() => setShowJson(!showJson)}>
              {showJson ? 'Hide JSON' : 'JSON'}
            </button>
          )}
        </div>
      </div>

      {/* Side-by-side mode tabs */}
      {data.group_id && siblingData && (
        <div className="result-mode-tabs" style={{ marginBottom: 8 }}>
          {[data, siblingData].map(d => (
            <button
              key={d.request_id}
              className={`result-mode-tab ${(activeMode || data.matching_mode) === d.matching_mode ? 'active' : ''}`}
              onClick={() => setActiveMode(d.matching_mode)}
            >
              {d.matching_mode?.toUpperCase()} Results
              <span className="tab-active-indicator">
                {d.state === 'COMPLETED' ? `${(d.results || []).reduce((s, r) => s + (r.matched_awards?.length || 0), 0)} matches` : d.state}
              </span>
            </button>
          ))}
        </div>
      )}

      {data.error_message && (
        <div className="error-box" style={{ marginBottom: 8, padding: '8px 12px', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Error: {data.error_message}</span>
          {data.state === 'FAILED' && (
            <button
              style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.15)', color: '#ef4444', cursor: 'pointer', fontSize: 12, fontWeight: 600, marginLeft: 12, whiteSpace: 'nowrap' }}
              onClick={async () => {
                setReprocessing(true);
                try {
                  await reprocessRequest(requestId);
                  const d = await getRequestDetail(requestId);
                  setData(d);
                } catch (err) {
                  alert('Reprocess failed: ' + (err.response?.data?.error || err.message));
                } finally { setReprocessing(false); }
              }}
              disabled={reprocessing}
            >
              {reprocessing ? 'Reprocessing...' : 'Reprocess'}
            </button>
          )}
        </div>
      )}

      {data.state === 'COMPLETED' && data.failed_expenditure_count > 0 && (
        <div className="error-box" style={{ marginBottom: 8, padding: '8px 12px', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(245,158,11,0.1)', borderColor: 'rgba(245,158,11,0.4)' }}>
          <span style={{ color: '#f59e0b' }}>
            {data.failed_expenditure_count} of {expenditures.length} expenditure(s) failed during processing
          </span>
          <button
            style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', cursor: 'pointer', fontSize: 12, fontWeight: 600, marginLeft: 12, whiteSpace: 'nowrap' }}
            onClick={async () => {
              setReprocessing(true);
              try {
                const failedIds = (results || []).filter(r => r.error).map(r => r.exp_id);
                await reprocessRequest(requestId, failedIds.length > 0 ? failedIds : undefined);
                const d = await getRequestDetail(requestId);
                setData(d);
              } catch (err) {
                alert('Retry failed: ' + (err.response?.data?.error || err.message));
              } finally { setReprocessing(false); }
            }}
            disabled={reprocessing}
          >
            {reprocessing ? 'Retrying...' : 'Retry Failed'}
          </button>
        </div>
      )}

      {(displayData.state === 'RECEIVED' || displayData.state === 'PROCESSING') && (
        <div className="processing-indicator" style={{ padding: 24 }}>
          <div className="spinner" />
          <span>Processing ({mode})...</span>
        </div>
      )}

      {displayData.state === 'COMPLETED' && !showJson && (
        <div className="detail-results">
          {/* Panel tabs */}
          <div className="detail-panel-tabs">
            <button className={`detail-panel-tab ${activePanel === 'matched' ? 'active' : ''}`} onClick={() => setActivePanel('matched')}>
              Expenditures ({expenditures.length})
            </button>
            <button className={`detail-panel-tab ${activePanel === 'usage' ? 'active' : ''}`} onClick={() => setActivePanel('usage')}>
              Usage
            </button>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
              <span>{results.reduce((s, r) => s + (r.matched_awards?.length || 0), 0)} total matches</span>
              <span>&middot;</span>
              <span>{results.filter(r => r.unmatched_expenditure).length} unmatched</span>
            </div>
          </div>

          {/* Expenditure List */}
          {activePanel === 'matched' && (
            <div className="detail-panel">
              <div className="expenditure-list">
                {pagedExps.map((exp, localIdx) => {
                  const globalIdx = pageStart + localIdx;
                  const result      = results[globalIdx] || {};
                  const matched     = result.matched_awards || [];
                  const unmatched   = result.unmatched_awards || [];
                  const unrestricted = result.unrestricted_awards || [];
                  const totalCandidates = result.candidate_count ?? (matched.length + unmatched.length + unrestricted.length);
                  const isExpanded  = expandedExp === globalIdx;
                  const topMatch    = matched.find(a => a.rank === 1);

                  const shareUrl = displayData.state === 'COMPLETED' && exp.exp_id
                    ? buildShareUrl(displayData.request_id, exp.exp_id)
                    : null;

                  return (
                    <div key={exp.exp_id || globalIdx} className={`expenditure-row ${isExpanded ? 'expanded' : ''}`}>
                      <div
                        className="expenditure-row-header"
                        onClick={() => { setExpandedExp(isExpanded ? null : globalIdx); setExpandedAward(null); }}
                      >
                        {/* Line number + collapse icon */}
                        <span style={{ fontSize: 10, color: 'var(--text-muted)', minWidth: 28, textAlign: 'right', flexShrink: 0 }}>
                          {pageStart + localIdx + 1}
                        </span>
                        <span className="exp-collapse-icon">{isExpanded ? '▼' : '▶'}</span>

                        {/* Primary identifiers */}
                        <span className="exp-id mono">{exp.exp_id}</span>
                        {exp.amt != null && <span className="exp-amount">${exp.amt.toLocaleString()}</span>}

                        {/* Category + enriched details */}
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {exp.exp_cat || '—'}
                            {exp.exp_type && (
                              <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 3, padding: '0 4px' }}>
                                {exp.exp_type}
                              </span>
                            )}
                          </span>
                          {(exp.task_org_name || exp.proj_name) && (
                            <span style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {exp.task_org_name && <span>Org: <strong style={{ color: 'var(--text)' }}>{exp.task_org_name}</strong></span>}
                              {exp.task_org_name && exp.proj_name && <span style={{ margin: '0 5px' }}>·</span>}
                              {exp.proj_name && <span>Proj: <strong style={{ color: 'var(--text)' }}>{exp.proj_name}</strong></span>}
                            </span>
                          )}
                        </span>

                        {/* Award hint */}
                        {exp.awd_num && (
                          <span className="exp-award mono" title="Award hint">{exp.awd_num}</span>
                        )}

                        {/* Match summary */}
                        <span className="exp-match-summary">
                          {result.error ? (
                            <span style={{ color: '#ef4444', fontWeight: 600 }}>Error</span>
                          ) : matched.length > 0 ? (
                            <span style={{ color: 'var(--green)' }}>{matched.length} match{matched.length !== 1 ? 'es' : ''}</span>
                          ) : (
                            <span style={{ color: 'var(--red)' }}>No match</span>
                          )}
                          {topMatch && (
                            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                              Top: <span className="mono" style={{ color: 'var(--accent-hover)' }}>{topMatch.awd_num}</span>
                              {' '}({topMatch.total_score?.toFixed(1)})
                            </span>
                          )}
                          {!result.error && (
                            <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                              {totalCandidates} candidates
                            </span>
                          )}
                        </span>

                        {shareUrl && (
                          <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                            <a
                              href={shareUrl} target="_blank" rel="noopener noreferrer"
                              className="detail-toggle-btn"
                              style={{ fontSize: 10, padding: '2px 8px', textDecoration: 'none' }}
                              title="Open in new tab"
                            >
                              View ↗
                            </a>
                            <button
                              className="detail-toggle-btn"
                              style={{ fontSize: 10, padding: '2px 8px' }}
                              title="Copy shareable link"
                              onClick={() => {
                                navigator.clipboard.writeText(shareUrl).then(() => {
                                  setCopiedExpId(exp.exp_id);
                                  setTimeout(() => setCopiedExpId(null), 2000);
                                });
                              }}
                            >
                              {copiedExpId === exp.exp_id ? '✓ Copied' : '🔗 Share'}
                            </button>
                          </span>
                        )}
                      </div>

                      {isExpanded && (
                        <div className="expenditure-row-body">
                          {result.error && (
                            <div className="error-box" style={{ marginBottom: 8, padding: '6px 10px', fontSize: 11 }}>
                              Processing failed: {result.error_message || 'Unknown error'}
                            </div>
                          )}

                          <div className="exp-detail-grid">
                            <KV label="Type"         value={exp.exp_type} />
                            <KV label="Organization" value={exp.task_org_name} />
                            <KV label="Project"      value={exp.proj_name} />
                            <KV label="Task"         value={exp.task_name} />
                            <KV label="Award Hint"   value={exp.awd_num} />
                            <KV label="Description"  value={exp.exp_txn_long_desc} />
                          </div>

                          {matched.length > 0 && (
                            <div className="data-table-wrap" style={{ marginTop: 10 }}>
                              <table className="data-table compact">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Award</th>
                                    <th>Name</th>
                                    <th title="Name match score">Nm</th>
                                    <th title="Purpose match score">Pur</th>
                                    <th title="Program match score">Prg</th>
                                    <th title="Restriction bonus">Rst</th>
                                    <th title="Age bonus">Age</th>
                                    <th title={modeTableTooltip(mode)}>
                                      {modeShortLabel(mode)}
                                    </th>
                                    <th>Total</th>
                                    <th title="Rule combination matched">Rule</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {matched.map(a => (
                                    <React.Fragment key={a.awd_num}>
                                      <tr
                                        className={expandedAward === `${globalIdx}-${a.awd_num}` ? 'row-expanded' : ''}
                                        onClick={() => setExpandedAward(expandedAward === `${globalIdx}-${a.awd_num}` ? null : `${globalIdx}-${a.awd_num}`)}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <td>
                                          <span className={`rank-dot ${a.rank === 1 ? 'gold' : a.rank === 2 ? 'silver' : a.rank === 3 ? 'bronze' : 'default'}`}>
                                            {a.rank}
                                          </span>
                                        </td>
                                        <td className="mono" style={{ fontWeight: 600 }}>{a.awd_num}</td>
                                        <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_name}</td>
                                        <td>{fmtS(a.name_match_score)}</td>
                                        <td>{fmtS(a.purpose_match_score)}</td>
                                        <td>{fmtS(a.program_match_score)}</td>
                                        <td>{fmtS(a.restriction_score)}</td>
                                        <td>{fmtS(a.age_score)}</td>
                                        <td>{fmtS(a.semantic_score)}</td>
                                        <td style={{ fontWeight: 800, color: 'var(--accent-hover)', whiteSpace: 'nowrap' }}>
                                          {a.total_score?.toFixed(2)}
                                          {a.confidence && (
                                            <span className={`confidence-badge ${a.confidence}`} style={{ marginLeft: 4 }}>{a.confidence}</span>
                                          )}
                                        </td>
                                        <td style={{ fontSize: 10, color: 'var(--text-muted)' }}>{getRuleLabel(a)}</td>
                                      </tr>
                                      {expandedAward === `${globalIdx}-${a.awd_num}` && (
                                        <tr className="expanded-row">
                                          <td colSpan={11}>
                                            <div className="expanded-detail">
                                              <div className="kv-grid" style={{ fontSize: 12 }}>
                                                <KV label="Organization" value={a.awd_org} />
                                                <KV label="Start Date"   value={a.awd_start} />
                                                <KV label="Purpose"      value={a.awd_purpose} />
                                                <KV label="Restriction"  value={a.awd_restrict} />
                                                <KV label="Control"      value={a.awd_ctrl} />
                                                <KV label="Employee"     value={a.awd_role_emp} />
                                                <KV label="Person (ORDS)" value={a.awd_person} />
                                                <KV label="Purpose (ORDS)" value={a.awd_purpose_desc} />
                                                <KV label="Program (ORDS)" value={a.awd_program} />
                                              </div>
                                              {a.awd_fund_auth && (
                                                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                                                  <strong>Fund Auth:</strong> {a.awd_fund_auth}
                                                </div>
                                              )}
                                              {a.match_reasons?.length > 0 && (
                                                <div style={{ marginTop: 8 }}>
                                                  <div className="match-evidence-header">
                                                    Match Evidence
                                                    {a.confidence && (
                                                      <span className={`confidence-badge ${a.confidence}`} style={{ marginLeft: 6 }}>{a.confidence} confidence</span>
                                                    )}
                                                  </div>
                                                  <ol className="match-reasons-list">
                                                    {a.match_reasons.map((reason, i) => (
                                                      <li key={i} className="match-reason-item">{reason}</li>
                                                    ))}
                                                  </ol>
                                                </div>
                                              )}
                                              <MatchDetails details={a.match_details} />
                                              {a.matched_fields?.length > 0 && (
                                                <div className="matched-fields-list" style={{ marginTop: 6 }}>
                                                  {a.matched_fields.map((f, i) => <span key={i} className="field-tag">{f}</span>)}
                                                </div>
                                              )}
                                              {(a.bm25_score != null || a.vector_score != null || a.os_score != null) && (
                                                <div style={{ marginTop: 8, padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: 11 }}>
                                                  <strong style={{ color: 'var(--accent-hover)' }}>Hybrid OS breakdown:</strong>{' '}
                                                  <span title="OpenSearch BM25 raw score">BM25 <span className="mono">{Number(a.bm25_score || 0).toFixed(3)}</span></span>
                                                  {' · '}
                                                  <span title="kNN cosine similarity (raw OS score)">Vector <span className="mono">{Number(a.vector_score || 0).toFixed(3)}</span></span>
                                                  {' · '}
                                                  <span title="Fused score after normalization">Fused <span className="mono">{Number(a.os_score || 0).toFixed(3)}</span></span>
                                                </div>
                                              )}
                                              {a.highlight && (
                                                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                                                  <strong style={{ color: 'var(--accent-hover)' }}>Match snippet:</strong>{' '}
                                                  <span dangerouslySetInnerHTML={{ __html: (a.highlight.title?.[0] || a.highlight.description?.[0] || a.highlight.restrictions?.[0] || '') }} />
                                                </div>
                                              )}
                                              {a.explanation && (
                                                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                                  <strong style={{ fontStyle: 'normal', color: 'var(--accent-hover)' }}>AI:</strong> {a.explanation}
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {(unmatched.length > 0 || unrestricted.length > 0) && (
                            <div style={{ marginTop: 8 }}>
                              <div
                                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', padding: '4px 0' }}
                                onClick={() => setShowUnmatchedExp(prev => ({ ...prev, [globalIdx]: !prev[globalIdx] }))}
                              >
                                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
                                  {showUnmatchedExp[globalIdx] ? '▼' : '▶'} All Candidates ({matched.length + unmatched.length + unrestricted.length})
                                </span>
                                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                  {matched.length} matched · {unmatched.length} not matched{unrestricted.length > 0 ? ` · ${unrestricted.length} unrestricted` : ''}
                                </span>
                              </div>
                              {showUnmatchedExp[globalIdx] && (
                                <div className="data-table-wrap" style={{ marginTop: 4 }}>
                                  <table className="data-table compact">
                                    <thead>
                                      <tr>
                                        <th>Status</th>
                                        <th>Award</th>
                                        <th>Name</th>
                                        <th>Org</th>
                                        <th>Purpose</th>
                                        <th>Restriction</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {matched.map(a => {
                                        const candKey = `${globalIdx}-${a.awd_num}`;
                                        return (
                                          <React.Fragment key={`m-${a.awd_num}`}>
                                            <tr
                                              style={{ background: 'rgba(16,185,129,0.05)', cursor: 'pointer' }}
                                              onClick={() => setExpandedCandidateExp(prev => ({ ...prev, [candKey]: !prev[candKey] }))}
                                            >
                                              <td><span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 700 }}>#{a.rank} matched {expandedCandidateExp[candKey] ? '▼' : '▶'}</span></td>
                                              <td className="mono" style={{ fontWeight: 600 }}>{a.awd_num}</td>
                                              <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_name}</td>
                                              <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>{a.awd_org}</td>
                                              <td style={{ fontSize: 10, color: 'var(--text-muted)' }}>{a.awd_purpose}</td>
                                              <td style={{ fontSize: 10, color: 'var(--text-muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_restrict}</td>
                                            </tr>
                                            {expandedCandidateExp[candKey] && (
                                              <tr className="expanded-row">
                                                <td colSpan={6}>
                                                  <div className="expanded-detail">
                                                    <div className="kv-grid" style={{ fontSize: 12 }}>
                                                      <KV label="Organization" value={a.awd_org} />
                                                      <KV label="Start Date" value={a.awd_start} />
                                                      <KV label="Purpose" value={a.awd_purpose} />
                                                      <KV label="Restriction" value={a.awd_restrict} />
                                                      <KV label="Control" value={a.awd_ctrl} />
                                                      <KV label="Employee" value={a.awd_role_emp} />
                                                      <KV label="Person (ORDS)" value={a.awd_person} />
                                                      <KV label="Purpose (ORDS)" value={a.awd_purpose_desc} />
                                                      <KV label="Program (ORDS)" value={a.awd_program} />
                                                      <KV label="Score" value={a.total_score?.toFixed(2)} />
                                                    </div>
                                                    {a.awd_fund_auth && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}><strong>Fund Auth:</strong> {a.awd_fund_auth}</div>}
                                                    {a.match_reasons?.length > 0 && (
                                                      <div style={{ marginTop: 8 }}>
                                                        <div className="match-evidence-header">Match Evidence</div>
                                                        <ol className="match-reasons-list">
                                                          {a.match_reasons.map((r, i) => <li key={i} className="match-reason-item">{r}</li>)}
                                                        </ol>
                                                      </div>
                                                    )}
                                                    <MatchDetails details={a.match_details} />
                                                    {a.explanation && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}><strong style={{ fontStyle: 'normal', color: 'var(--accent-hover)' }}>AI:</strong> {a.explanation}</div>}
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                      {unmatched.map((a, i) => {
                                        const uKey = `${globalIdx}-u-${a.awd_num || i}`;
                                        return (
                                          <React.Fragment key={uKey}>
                                            <tr
                                              style={{ opacity: 0.75, cursor: 'pointer' }}
                                              onClick={() => setExpandedCandidateExp(prev => ({ ...prev, [uKey]: !prev[uKey] }))}
                                            >
                                              <td><span style={{ fontSize: 10, color: 'var(--text-muted)' }}>not matched {expandedCandidateExp[uKey] ? '▼' : '▶'}</span></td>
                                              <td className="mono">{a.awd_num}</td>
                                              <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_name}</td>
                                              <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>{a.awd_org}</td>
                                              <td style={{ fontSize: 10, color: 'var(--text-muted)' }}>{a.awd_purpose}</td>
                                              <td style={{ fontSize: 10, color: 'var(--text-muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.awd_restrict}</td>
                                            </tr>
                                            {expandedCandidateExp[uKey] && (
                                              <tr className="expanded-row">
                                                <td colSpan={6}>
                                                  <div className="expanded-detail">
                                                    <div className="kv-grid" style={{ fontSize: 12 }}>
                                                      <KV label="Organization" value={a.awd_org} />
                                                      <KV label="Start Date" value={a.awd_start} />
                                                      <KV label="Purpose" value={a.awd_purpose} />
                                                      <KV label="Restriction" value={a.awd_restrict} />
                                                      <KV label="Control" value={a.awd_ctrl} />
                                                      <KV label="Employee" value={a.awd_role_emp} />
                                                      <KV label="Person (ORDS)" value={a.awd_person} />
                                                      <KV label="Purpose (ORDS)" value={a.awd_purpose_desc} />
                                                      <KV label="Program (ORDS)" value={a.awd_program} />
                                                    </div>
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                      {unrestricted.map((a, i) => (
                                        <tr key={`r-${a.awd_num || i}`} style={{ opacity: 0.5 }}>
                                          <td><span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>unrestricted</span></td>
                                          <td className="mono">{a.awd_num}</td>
                                          <td colSpan={4} style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>excluded from scoring (unrestricted award)</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          )}

                          {result.unmatched_expenditure && (
                            <div className="warning-box" style={{ marginTop: 8, padding: '6px 10px', fontSize: 11 }}>
                              Expenditure unmatched. {result.unmatched_expenditure_reason}
                            </div>
                          )}

                          {matched.length === 0 && !result.unmatched_expenditure && (
                            <div className="empty-state" style={{ padding: 12 }}>No matching awards found.</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Pagination bar */}
              {totalExp > 0 && (
                <Paginator
                  total={totalExp}
                  page={expPage}
                  pageSize={expPageSize}
                  onPage={p => { setExpPage(p); setExpandedExp(null); setExpandedAward(null); }}
                  onPageSize={s => { setExpPageSize(s); setExpPage(0); setExpandedExp(null); setExpandedAward(null); }}
                />
              )}
            </div>
          )}

          {/* Usage Panel */}
          {activePanel === 'usage' && (
            <div className="detail-panel">
              <div className="card" style={{ padding: 14 }}>
                {(mode === 'hybrid-os' || mode === 'hybrid-os-judge') && (displayData.vector_usage || displayData.llm_usage) ? (
                  <>
                    <div className="kv-grid" style={{ fontSize: 12 }}>
                      <KV label="Method" value="OpenSearch Hybrid (BM25 + kNN)" />
                      <KV label="Processing Time" value={displayData.processing_time_ms ? `${displayData.processing_time_ms}ms` : null} />
                      <KV label="Expenditures" value={expenditures.length} />
                    </div>
                    {displayData.vector_usage && (
                    <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>SEMANTIC VECTOR (kNN HNSW)</div>
                      <div className="kv-grid" style={{ fontSize: 12 }}>
                        <KV label="Embedding Model"      value={displayData.vector_usage.embedding_model} />
                        <KV label="Candidates Indexed"   value={displayData.vector_usage.candidates_embedded?.toLocaleString()} />
                        <KV label="Dimensions"           value={displayData.vector_usage.dimensions?.toLocaleString()} />
                        <KV label="Embedding Tokens"     value={displayData.vector_usage.total_tokens?.toLocaleString()} />
                      </div>
                    </div>
                    )}
                    <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>BM25 KEYWORD SEARCH</div>
                      <div className="kv-grid" style={{ fontSize: 12 }}>
                        <KV label="Engine"  value="OpenSearch (Lucene + synonym analyzer)" />
                        <KV label="Fusion"  value={`Weighted (α·BM25 + β·Vector)`} />
                      </div>
                    </div>
                    {displayData.llm_usage && (
                      <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                          {mode === 'hybrid-os-judge' ? 'LLM SCORER + JUDGE' : 'LLM RERANK'}
                        </div>
                        <div className="kv-grid" style={{ fontSize: 12 }}>
                          <KV label="Model"             value={displayData.llm_usage.model} />
                          <KV label="Prompt Tokens"     value={displayData.llm_usage.prompt_tokens?.toLocaleString()} />
                          <KV label="Completion Tokens" value={displayData.llm_usage.completion_tokens?.toLocaleString()} />
                          <KV label="Total Tokens"      value={displayData.llm_usage.total_tokens?.toLocaleString()} />
                          {displayData.llm_usage.total_cost_usd != null && (
                            <>
                              <KV label="Prompt Cost"     value={`$${displayData.llm_usage.prompt_cost_usd?.toFixed(6)}`} />
                              <KV label="Completion Cost" value={`$${displayData.llm_usage.completion_cost_usd?.toFixed(6)}`} />
                              <KV label="Total Cost" value={`$${displayData.llm_usage.total_cost_usd?.toFixed(6)} USD${displayData.llm_usage.pricing_tier === 'long-context' ? ' · long-context' : ''}`} />
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : mode === 'hybrid' && displayData.vector_usage ? (
                  <>
                    <div className="kv-grid" style={{ fontSize: 12 }}>
                      <KV label="Method" value="Hybrid RAG (Vector + Keyword + RRF)" />
                      <KV label="Processing Time" value={displayData.processing_time_ms ? `${displayData.processing_time_ms}ms` : null} />
                      <KV label="Expenditures" value={expenditures.length} />
                    </div>
                    <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>SEMANTIC VECTOR SEARCH</div>
                      <div className="kv-grid" style={{ fontSize: 12 }}>
                        <KV label="Embedding Model"      value={displayData.vector_usage.embedding_model} />
                        <KV label="Candidates Embedded"  value={displayData.vector_usage.candidates_embedded?.toLocaleString()} />
                        <KV label="Dimensions"           value={displayData.vector_usage.dimensions?.toLocaleString()} />
                        <KV label="Embedding Tokens"     value={displayData.vector_usage.total_tokens?.toLocaleString()} />
                      </div>
                    </div>
                    <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>KEYWORD FULL-TEXT SEARCH</div>
                      <div className="kv-grid" style={{ fontSize: 12 }}>
                        <KV label="Engine" value="PostgreSQL tsvector/tsquery" />
                        <KV label="Fusion" value="Reciprocal Rank Fusion (RRF, k=60)" />
                      </div>
                    </div>
                  </>
                ) : mode === 'vector-llm' && (displayData.vector_usage || displayData.llm_usage) ? (
                  <>
                    <div className="kv-grid" style={{ fontSize: 12 }}>
                      <KV label="Method" value="Vector + LLM (Retrieval then Reasoning)" />
                      <KV label="Processing Time" value={displayData.processing_time_ms ? `${displayData.processing_time_ms}ms` : null} />
                      <KV label="Expenditures" value={expenditures.length} />
                    </div>
                    {displayData.vector_usage && (
                      <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>VECTOR RETRIEVAL STAGE</div>
                        <div className="kv-grid" style={{ fontSize: 12 }}>
                          <KV label="Embedding Model"     value={displayData.vector_usage.embedding_model} />
                          <KV label="Candidates Embedded" value={displayData.vector_usage.candidates_embedded?.toLocaleString()} />
                          <KV label="Dimensions"          value={displayData.vector_usage.dimensions?.toLocaleString()} />
                          <KV label="Embedding Tokens"    value={displayData.vector_usage.total_tokens?.toLocaleString()} />
                        </div>
                      </div>
                    )}
                    {displayData.llm_usage && (
                      <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>LLM SCORING STAGE</div>
                        <div className="kv-grid" style={{ fontSize: 12 }}>
                          <KV label="Model"              value={displayData.llm_usage.model} />
                          <KV label="Endpoint"           value={displayData.llm_usage.litellm_base_url} />
                          <KV label="Prompt Tokens"      value={displayData.llm_usage.prompt_tokens?.toLocaleString()} />
                          <KV label="Completion Tokens"  value={displayData.llm_usage.completion_tokens?.toLocaleString()} />
                          <KV label="Total Tokens"       value={displayData.llm_usage.total_tokens?.toLocaleString()} />
                        </div>
                      </div>
                    )}
                  </>
                ) : mode === 'vector' && displayData.vector_usage ? (
                  <div className="kv-grid" style={{ fontSize: 12 }}>
                    <KV label="Method"              value="Vector RAG (pgvector)" />
                    <KV label="Embedding Model"     value={displayData.vector_usage.embedding_model} />
                    <KV label="Candidates Embedded" value={displayData.vector_usage.candidates_embedded?.toLocaleString()} />
                    <KV label="Dimensions"          value={displayData.vector_usage.dimensions?.toLocaleString()} />
                    <KV label="Total Tokens"        value={displayData.vector_usage.total_tokens?.toLocaleString()} />
                    <KV label="Processing Time"     value={displayData.processing_time_ms ? `${displayData.processing_time_ms}ms` : null} />
                    <KV label="Expenditures"        value={expenditures.length} />
                  </div>
                ) : mode === 'llm' && displayData.llm_usage ? (
                  <>
                    <div className="kv-grid" style={{ fontSize: 12 }}>
                      <KV label="Method"             value={displayData.llm_chunk_log?.length > 1 ? 'LLM Chunked Recursive' : 'LLM Direct Prompt'} />
                      <KV label="Model"              value={displayData.llm_usage.model} />
                      <KV label="Endpoint"           value={displayData.llm_usage.litellm_base_url} />
                      <KV label="Prompt Tokens"      value={displayData.llm_usage.prompt_tokens?.toLocaleString()} />
                      <KV label="Completion Tokens"  value={displayData.llm_usage.completion_tokens?.toLocaleString()} />
                      <KV label="Total Tokens"       value={displayData.llm_usage.total_tokens?.toLocaleString()} />
                      <KV label="Processing Time"    value={displayData.processing_time_ms ? `${displayData.processing_time_ms}ms` : null} />
                      <KV label="Expenditures"       value={expenditures.length} />
                      {displayData.llm_chunk_log?.length > 0 && (
                        <KV label="LLM Rounds" value={`${Math.max(...displayData.llm_chunk_log.map(c => c.round))} (${displayData.llm_chunk_log.length} calls)`} />
                      )}
                    </div>
                    {displayData.llm_chunk_log?.length > 1 && (
                      <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>CHUNK LOG</div>
                        <table className="data-table compact" style={{ fontSize: 11 }}>
                          <thead>
                            <tr>
                              <th>Round</th><th>Type</th><th>Exp</th><th>Chunk</th>
                              <th>In</th><th>Out</th><th>Tokens</th><th>Top Awards</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayData.llm_chunk_log.map((c, i) => (
                              <tr key={i} style={{ cursor: 'default' }}>
                                <td>{c.round}</td>
                                <td><span className={`badge ${c.type === 'final' ? 'completed' : 'processing'}`}>{c.type}</span></td>
                                <td>{c.exp_id || '-'}</td>
                                <td>{c.chunk_index ? `${c.chunk_index}/${c.chunk_total}` : '-'}</td>
                                <td>{c.candidates_in}</td>
                                <td>{c.candidates_out}</td>
                                <td>{c.tokens?.toLocaleString()}</td>
                                <td className="mono" style={{ fontSize: 10 }}>{c.top_awards?.join(', ') || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-state" style={{ padding: 16 }}>No usage data available.</div>
                )}
                {displayData.state_history?.length > 0 && (
                  <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>TIMELINE</div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11 }}>
                      {displayData.state_history.map((h, i) => (
                        <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span className={`timeline-dot-inline ${h.state?.toLowerCase()}`} />
                          <span style={{ fontWeight: 600 }}>{h.state}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{new Date(h.timestamp).toLocaleTimeString()}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {showJson && resultJson && (
        <div className="json-viewer" style={{ maxHeight: 'calc(100vh - 160px)' }}>
          {JSON.stringify(resultJson, null, 2)}
        </div>
      )}
    </div>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function Paginator({ total, page, pageSize, onPage, onPageSize }) {
  const totalPages = Math.ceil(total / pageSize) || 1;
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to   = Math.min((page + 1) * pageSize, total);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
      fontSize: 11, color: 'var(--text-muted)',
      borderTop: '1px solid var(--border)', flexWrap: 'wrap',
      background: 'var(--bg-card)',
    }}>
      <span>Rows per page:</span>
      {[10, 25].map(size => (
        <button
          key={size}
          onClick={() => { onPageSize(size); onPage(0); }}
          style={{
            padding: '1px 9px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
            border: '1px solid var(--border)',
            background: pageSize === size ? 'var(--accent)' : 'transparent',
            color: pageSize === size ? '#fff' : 'var(--text-muted)',
            fontWeight: pageSize === size ? 700 : 400,
          }}
        >{size}</button>
      ))}
      <span style={{ marginLeft: 6 }}>{from}–{to} of {total}</span>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
        <button
          disabled={page === 0}
          onClick={() => onPage(page - 1)}
          style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}
        >‹ Prev</button>
        <span style={{ fontSize: 11, padding: '0 4px' }}>{page + 1} / {totalPages}</span>
        <button
          disabled={page >= totalPages - 1}
          onClick={() => onPage(page + 1)}
          style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}
        >Next ›</button>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="kv-row">
      <span className="kv-label">{label}</span>
      <span className="kv-value">{value}</span>
    </div>
  );
}

function fmtS(val) {
  if (val == null) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
  const n = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(n)) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
  const display = Number.isInteger(n) ? n.toString() : n.toFixed(2);
  return <span style={{ fontWeight: n > 0 ? 700 : 400, color: n > 0 ? 'var(--green)' : 'var(--text-muted)' }}>{display}</span>;
}

function getRuleLabel(award) {
  const hasName    = (award.name_match_score    || 0) > 0;
  const hasPurpose = (award.purpose_match_score || 0) > 0;
  const hasProgram = (award.program_match_score || 0) > 0;
  if (hasName && hasPurpose && hasProgram) return 'N+P+Prg';
  if (hasName && (hasPurpose || hasProgram)) return `N+${hasPurpose ? 'P' : 'Prg'}`;
  if (hasName) return 'Name';
  if (hasPurpose && hasProgram) return 'P+Prg';
  if (hasPurpose) return 'Purpose';
  if (hasProgram) return 'Program';
  return 'Semantic';
}
